import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Card, 
  Form, 
  Select, 
  DatePicker, 
  TimePicker, 
  Input, 
  Button, 
  Typography, 
  Row, 
  Col, 
  Space, 
  Alert, 
  Modal, 
  message,
  Tag,
  Divider
} from 'antd';
import { 
  CalendarOutlined, 
  ClockCircleOutlined, 
  CheckCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { doc, updateDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { loadGoogleMaps, isGoogleMapsReady } from '../../../utils/googleMapsLoader';
// Customer portal service removed - using mock data
const serviceTypes = [
  {
    id: 'lawn-care',
    name: 'Lawn Care',
    description: 'Mowing, edging, and lawn maintenance',
    duration: 60,
    price: 75,
    icon: '🌱'
  },
  {
    id: 'painting',
    name: 'Painting',
    description: 'Interior and exterior painting services',
    duration: 240,
    price: 200,
    icon: '🎨'
  },
  {
    id: 'cleaning',
    name: 'Deep Cleaning',
    description: 'Comprehensive home cleaning services',
    duration: 180,
    price: 150,
    icon: '🧽'
  },
  {
    id: 'maintenance',
    name: 'General Maintenance',
    description: 'Various home maintenance tasks',
    duration: 120,
    price: 100,
    icon: '🔧'
  },
  {
    id: 'gutter-cleaning',
    name: 'Gutter Cleaning',
    description: 'Gutter cleaning and maintenance',
    duration: 90,
    price: 85,
    icon: '🏠'
  },
  {
    id: 'landscaping',
    name: 'Landscaping',
    description: 'Garden and landscape maintenance',
    duration: 150,
    price: 125,
    icon: '🌿'
  }
];

const getAvailableTimeSlots = (date) => {
  const slots = [];
  const startHour = 8; // 8 AM
  const endHour = 17; // 5 PM
  
  for (let hour = startHour; hour < endHour; hour++) {
    slots.push({
      time: `${hour}:00`,
      display: `${hour}:00 AM`,
      available: true
    });
    slots.push({
      time: `${hour}:30`,
      display: `${hour}:30 AM`,
      available: true
    });
  }
  
  return slots;
};
import { useAuth } from '../../../contexts/AuthContext';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const OnlineScheduling = ({ onSuccess, editMode = false, existingAppointment = null }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false);
  
  // Google Places autocomplete states
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressInputRef = useRef(null);
  const debounceRef = useRef(null);
  
  const { currentUser, userProfile } = useAuth();

  // Initialize Google Maps
  useEffect(() => {
    const initializeGoogleMaps = async () => {
      try {
        await loadGoogleMaps();
        console.log('Google Maps loaded successfully for OnlineScheduling');
      } catch (error) {
        console.error('Failed to load Google Maps:', error);
      }
    };

    initializeGoogleMaps();
  }, []);

  // Address handling with Google Places API
  const handleAddressChange = useCallback((e) => {
    const value = e.target.value;
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    if (value.length > 2 && isGoogleMapsReady()) {
      setAddressLoading(true);
      debounceRef.current = setTimeout(() => {
        const google = window.google;
        const service = new google.maps.places.AutocompleteService();
        const request = {
          input: value,
          types: ['address'],
          componentRestrictions: { country: 'us' }
        };
        
        service.getPlacePredictions(request, (predictions, status) => {
          setAddressLoading(false);
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setAddressSuggestions(predictions.map(prediction => prediction.description));
            setShowSuggestions(true);
          } else {
            setAddressSuggestions([]);
            setShowSuggestions(false);
          }
        });
      }, 300);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  const handleSuggestionClick = useCallback((suggestion) => {
    form.setFieldsValue({ address: suggestion });
    setAddressSuggestions([]);
    setShowSuggestions(false);
  }, [form]);

  const handleAddressFocus = useCallback(() => {
    if (addressSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  }, [addressSuggestions]);

  const handleAddressBlur = useCallback(() => {
    // Delay hiding suggestions to allow click events
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  }, []);

  // Populate form when in edit mode or set default date for campaigns
  useEffect(() => {
    if (editMode && existingAppointment) {
      console.log('📝 Populating form for edit mode:', existingAppointment);
      
      // Infer service ids from names when missing
      const nameArray = Array.isArray(existingAppointment.serviceNames)
        ? existingAppointment.serviceNames
        : typeof existingAppointment.serviceNames === 'string'
          ? existingAppointment.serviceNames.split(',').map(s => s.trim())
          : [];

      const nameToId = {
        'gutter cleaning': 'gutter-cleaning',
        'gutter-cleaning': 'gutter-cleaning',
        'gutter': 'gutter-cleaning',
        'power washing': 'cleaning',
        'power wash': 'cleaning',
        'pressure washing': 'cleaning',
        'junk removal': 'maintenance',
        'tree trimming': 'landscaping',
        'lawn care': 'lawn-care',
        'painting': 'painting',
        'general maintenance': 'maintenance',
        'landscaping': 'landscaping',
        'deep cleaning': 'cleaning'
      };

      const inferredTypes = nameArray
        .map(n => nameToId[(n || '').toLowerCase()])
        .filter(Boolean);

      // Parse the existing appointment data
      const serviceIds = (existingAppointment.serviceTypes && existingAppointment.serviceTypes.length)
        ? existingAppointment.serviceTypes
        : (existingAppointment.serviceType ? [existingAppointment.serviceType] : inferredTypes);
      const services = serviceTypes.filter(s => (serviceIds || []).includes(s.id));
      
      // Set form values
      // Try multiple time formats
      const timeFormats = ['HH:mm', 'H:mm', 'h:mm A', 'hh:mm A'];
      let parsedTime = null;
      if (existingAppointment.scheduledTime) {
        for (const fmt of timeFormats) {
          const t = dayjs(existingAppointment.scheduledTime, fmt);
          if (t.isValid()) { parsedTime = t; break; }
        }
      }

      // Only set fields that exist on the record; do not auto-default
      const dateValue = existingAppointment.scheduledDate ? dayjs(existingAppointment.scheduledDate) : null;
      const timeValue = parsedTime;

      form.setFieldsValue({
        serviceType: serviceIds || [],
        date: dateValue,
        time: timeValue,
        address: existingAppointment.address || '',
        notes: existingAppointment.notes || ''
      });
      
      // Set component state
      setSelectedServices(services);
      if (dateValue) {
        setSelectedDate(dateValue);
        const slots = getAvailableTimeSlots(dateValue);
        setAvailableSlots(slots);
      }
    }
  }, [editMode, existingAppointment, form]);

  const handleServiceChange = (serviceIds) => {
    const services = serviceTypes.filter(s => serviceIds.includes(s.id));
    setSelectedServices(services);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      const slots = getAvailableTimeSlots(date);
      setAvailableSlots(slots);
    }
  };

  const handleCancelService = async () => {
    if (!editMode || !existingAppointment) return;
    
    try {
      console.log('🚫 Cancelling service:', existingAppointment.id);
      
      // Update appointment status to cancelled in Firestore
      const appointmentRef = doc(db, 'appointments', existingAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'cancelled',
        updatedAt: new Date()
      });
      
      console.log('✅ Service cancelled successfully');
      message.success('Service cancelled successfully!');
      
      // Close the cancel confirmation modal
      setCancelConfirmVisible(false);
      
      // Call the onSuccess callback to close modal and refresh
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error cancelling service:', error);
      message.error('Failed to cancel service. Please try again.');
    }
  };

  const handleSubmit = async (values) => {
    console.log('🚀 Form submission started');
    console.log('Form values received:', values);
    
    try {
      setLoading(true);
      
      // Debug logging
      console.log('Form values:', values);
      console.log('Form values.serviceType:', values.serviceType);
      console.log('Current user:', currentUser);
      console.log('User profile:', userProfile);
      console.log('Selected services:', selectedServices);
      
      // Validate required data
      if (!currentUser || !currentUser.uid) {
        throw new Error('User not authenticated');
      }
      
      if (!selectedServices || selectedServices.length === 0) {
        throw new Error('Please select at least one service');
      }
      
      if (!values.date || !values.time) {
        throw new Error('Please select date and time');
      }
      
      // Calculate total duration and price for multiple services
      const totalDuration = selectedServices.reduce((sum, service) => sum + service.duration, 0);
      const totalPrice = selectedServices.reduce((sum, service) => sum + service.price, 0);
      const serviceNames = selectedServices.map(service => service.name).join(', ');
      
      // Ensure serviceType is properly set (use the first service type or the array)
      const serviceTypeValue = Array.isArray(values.serviceType) ? values.serviceType[0] : values.serviceType;
      
      const appointmentData = {
        customerId: currentUser.uid,
        customerName: `${userProfile?.firstName || 'Unknown'} ${userProfile?.lastName || 'User'}`,
        customerEmail: userProfile?.email || currentUser.email || 'unknown@example.com',
        customerPhone: userProfile?.phone || 'Not provided',
        serviceType: serviceTypeValue, // Single service type (required by service)
        serviceTypes: values.serviceType, // Array of service IDs (for multiple services)
        serviceNames: serviceNames, // Comma-separated service names
        services: selectedServices, // Full service objects
        scheduledDate: values.date.format('YYYY-MM-DD'),
        scheduledTime: values.time.format('HH:mm'),
        estimatedDuration: totalDuration,
        estimatedPrice: totalPrice,
        notes: values.notes || '',
        address: userProfile?.address || values.address || 'Address not provided'
      };

      console.log('Appointment data to be saved:', appointmentData);
      
      if (editMode && existingAppointment) {
        console.log('📤 Updating existing appointment:', existingAppointment.id);
        // Update existing appointment
        const appointmentRef = doc(db, 'appointments', existingAppointment.id);
        await updateDoc(appointmentRef, {
          ...appointmentData,
          updatedAt: new Date()
        });
        console.log('✅ Appointment updated successfully');
      } else {
        console.log('📤 Creating new appointment...');
        // Create new appointment
        const appointmentsRef = collection(db, 'appointments');
        const docRef = await addDoc(appointmentsRef, {
          ...appointmentData,
          status: 'scheduled',
          createdAt: new Date(),
          updatedAt: new Date()
        });
        console.log('✅ Appointment created successfully with ID:', docRef.id);
      }
      
      setAppointmentDetails({
        ...appointment,
        services: selectedServices
      });
      setConfirmationVisible(true);
      
      form.resetFields();
      setSelectedServices([]);
      setSelectedDate(null);
      setAvailableSlots([]);
      
      message.success(editMode ? 'Appointment updated successfully!' : 'Appointment scheduled successfully!');
      
      // Call the onSuccess callback if provided (for modal close)
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error scheduling appointment:', error);
      message.error(`Failed to schedule appointment: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const disabledDate = (current) => {
    // No date restrictions - allow any date selection
    return false;
  };

  // Check if user is authenticated
  if (!currentUser) {
    return (
      <div>
        <Card>
          <Alert
            message="Authentication Required"
            description="Please sign in to schedule an appointment."
            type="warning"
            showIcon
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <Title level={4} style={{ 
          color: '#1890ff',
          fontFamily: 'Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontWeight: 'bold',
          marginBottom: 0
        }}>
          <CalendarOutlined style={{ color: '#1890ff', marginRight: '0.5rem' }} />
          {editMode ? 'Edit Your Service' : 'Schedule Your Service'}
        </Title>
        {editMode && (
          <div style={{ marginTop: 8 }}>
            <Space wrap>
              {existingAppointment?.quoteId && (
                <Tag color="blue">Booking ID: {existingAppointment.quoteId}</Tag>
              )}
              {(() => {
                const ps = (existingAppointment?.paymentStatus || existingAppointment?.status || '').toString().toLowerCase();
                if (!ps) return null;
                const color = ps.includes('succeed') || ps === 'paid' ? 'green' : ps.includes('fail') ? 'red' : (ps === 'scheduled' || ps === 'confirmed') ? 'blue' : 'gold';
                const label = `Payment: ${existingAppointment?.paymentStatus || existingAppointment?.status}`;
                return <Tag color={color}>{label}</Tag>;
              })()}
            </Space>
          </div>
        )}
        {false && (
          <Paragraph style={{ fontSize: '1rem', color: '#666', fontFamily: 'Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
            {editMode ? 'Update your service details below' : 'Book your home maintenance service in just a few clicks'}
          </Paragraph>
        )}
      </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            console.log('📝 Form onFinish triggered with values:', values);
            handleSubmit(values);
          }}
          onFinishFailed={(errorInfo) => {
            console.log('❌ Form validation failed:', errorInfo);
            console.log('Failed fields:', errorInfo.errorFields);
          }}
          size="middle"
        >
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="serviceType"
                label="Service Types"
                rules={[{ required: true, message: 'Please select at least one service' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select one or more services"
                  onChange={handleServiceChange}
                  size="middle"
                  maxTagCount="responsive"
                >
                  {serviceTypes.map(service => (
                    <Option key={service.id} value={service.id}>
                      <Space>
                        <span style={{ fontSize: '1.2rem' }}>{service.icon}</span>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{service.name}</div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            {service.description} - ${service.price}
                          </div>
                        </div>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="date"
                label="Preferred Date"
                rules={[{ required: true, message: 'Please select a date' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  size="large"
                  disabledDate={disabledDate}
                  onChange={handleDateChange}
                  placeholder="Select date"
                  suffixIcon={<CalendarOutlined style={{ cursor: 'pointer' }} />}
                  allowClear={false}
                  getPopupContainer={(trigger) => trigger.parentElement}
                />
              </Form.Item>
            </Col>
          </Row>

          {selectedServices.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 6,
              padding: '8px 12px',
              marginBottom: 8
            }}>
              <div style={{ fontWeight: 600, color: '#1890ff' }}>
                {selectedServices.map(s => s.name).join(', ')}
              </div>
              <div style={{ color: '#52c41a' }}>
                {selectedServices.reduce((sum, s) => sum + s.duration, 0)} mins · ${selectedServices.reduce((sum, s) => sum + s.price, 0)}
              </div>
            </div>
          )}

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="time"
                label="Preferred Time"
                rules={[{ required: true, message: 'Please select a time' }]}
              >
                <TimePicker
                  style={{ width: '100%' }}
                  size="middle"
                  format="HH:mm"
                  minuteStep={30}
                  placeholder="Select time"
                  disabled={!selectedDate}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="address"
                label="Service Address"
                rules={[{ required: true, message: 'Please enter service address' }]}
              >
                <div style={{ position: 'relative' }}>
                  <Input
                    ref={addressInputRef}
                    placeholder="Enter service address"
                    size="middle"
                    defaultValue={userProfile?.address}
                    onChange={handleAddressChange}
                    onFocus={handleAddressFocus}
                    onBlur={handleAddressBlur}
                    loading={addressLoading}
                  />
                  {showSuggestions && addressSuggestions.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 1000,
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}
                    >
                      {addressSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: index < addressSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                            fontSize: '14px',
                            color: '#262626'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f5f5f5';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'white';
                          }}
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Additional Notes (Optional)"
          >
            <TextArea
              rows={3}
              placeholder="Any special instructions or notes for our team..."
            />
          </Form.Item>

          <Divider />

              <div style={{ textAlign: 'center' }}>
                <Space size="large">
                  {editMode && (
                    <Button
                      type="default"
                      danger
                      size="large"
                      onClick={() => setCancelConfirmVisible(true)}
                      style={{
                        height: '50px',
                        padding: '0 30px',
                        borderRadius: '12px',
                        fontFamily: 'Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        fontWeight: 'bold',
                        fontSize: '16px'
                      }}
                    >
                      Cancel Service
                    </Button>
                  )}
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    size="large"
                    onClick={() => {
                      console.log('🔘 Schedule Appointment button clicked');
                      console.log('Form values before submit:', form.getFieldsValue());
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #FD865C, #ff9a6b)',
                      border: 'none',
                      height: '50px',
                      padding: '0 40px',
                      borderRadius: '12px',
                      fontFamily: 'Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: 'bold',
                      fontSize: '16px',
                      boxShadow: '0 4px 12px rgba(253, 134, 92, 0.3)'
                    }}
                  >
                    <CalendarOutlined style={{ marginRight: '0.5rem' }} />
                    {editMode ? 'Update Appointment' : 'Schedule Appointment'}
                  </Button>
                </Space>
              </div>
        </Form>

      {/* Confirmation Modal */}
      <Modal
        title="Appointment Confirmed!"
        open={confirmationVisible}
        onCancel={() => setConfirmationVisible(false)}
        footer={null}
        width={500}
        centered
      >
        {appointmentDetails && (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <CheckCircleOutlined style={{ fontSize: '4rem', color: '#52c41a', marginBottom: '1rem' }} />
            
            <Title level={4} style={{ color: '#52c41a' }}>
              Your appointment has been scheduled!
            </Title>
            
            <div style={{ textAlign: 'left', margin: '1.5rem 0' }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <Text strong>Service:</Text> {appointmentDetails.serviceName}
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <Text strong>Date:</Text> {dayjs(appointmentDetails.scheduledDate).format('MMMM DD, YYYY')}
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <Text strong>Time:</Text> {appointmentDetails.scheduledTime}
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <Text strong>Duration:</Text> {appointmentDetails.estimatedDuration} minutes
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <Text strong>Estimated Price:</Text> ${appointmentDetails.estimatedPrice}
              </div>
              <div>
                <Text strong>Status:</Text> <Tag color="blue">Scheduled</Tag>
              </div>
            </div>

            <Alert
              message="What's Next?"
              description="You'll receive a confirmation email shortly. Our team will contact you 24 hours before your appointment to confirm details."
              type="info"
              showIcon
              style={{ marginBottom: '1rem' }}
            />

            <Button 
              type="primary" 
              onClick={() => setConfirmationVisible(false)}
              size="large"
            >
              Close
            </Button>
          </div>
        )}
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        title="Cancel Service"
        open={cancelConfirmVisible}
        onOk={handleCancelService}
        onCancel={() => setCancelConfirmVisible(false)}
        okText="Yes, Cancel Service"
        cancelText="Keep Service"
        okButtonProps={{ danger: true }}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <Title level={4}>Are you sure you want to cancel this service?</Title>
          <Paragraph>
            This action cannot be undone. Your service will be cancelled and you'll need to schedule a new one if you change your mind.
          </Paragraph>
          {existingAppointment && (
            <div style={{ 
              background: '#f5f5f5', 
              padding: '12px', 
              borderRadius: '8px',
              marginTop: '16px'
            }}>
              <Text strong>Service Details:</Text>
              <br />
              <Text>{existingAppointment.serviceNames || existingAppointment.serviceType}</Text>
              <br />
              <Text type="secondary">
                {existingAppointment.scheduledDate} at {existingAppointment.scheduledTime}
              </Text>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default OnlineScheduling;
